import React, { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, UserPlus } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

const LawyerCreateClient = () => {
    const [showAddClientModal, setShowAddClientModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [clients, setClients] = useState([]);

    const [newClientData, setNewClientData] = useState({
        name: "",
        email: "",
        phone: "",
    });

    const handleAddClient = async (e) => {
        e.preventDefault();
        setSaving(true);

        const token = localStorage.getItem("auth_token");
        if (!token) {
            Swal.fire("Error", "Authentication required.", "error");
            setSaving(false);
            return;
        }

        const payload = {
            name: newClientData.name.trim(),
            email: newClientData.email.trim(),
            phone: newClientData.phone.trim(),
        };

        try {
            const response = await api.post("/clients", payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.data.success && response.data.data) {
                const newClient = response.data.data;

                setClients((prev) => [...prev, newClient]);

                setShowAddClientModal(false);
                setNewClientData({ name: "", email: "", phone: "" });

                Swal.fire({
                    icon: "success",
                    title: "Client Added!",
                    html: `
                        <p class="text-sm">Account created successfully.</p>
                        <p class="text-sm mt-2">Verification email sent to:</p>
                        <p class="font-medium">${newClient.email}</p>
                        <div class="mt-3 p-3 bg-blue-50 rounded-lg text-left">
                            <p class="text-xs font-medium text-blue-900">Temporary Password:</p>
                            <p class="font-mono text-sm text-blue-700">${newClient.temporary_password}</p>
                            <p class="text-xs text-blue-600 mt-1">Will be sent via email</p>
                        </div>
                    `,
                    confirmButtonColor: "#1e40af",
                    timer: 6000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to create client.";
            Swal.fire("Error", msg, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6">
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">My Clients</h2>
                        <p className="text-gray-600 mt-1">Create and manage client accounts</p>
                    </div>
                    <motion.button
                        onClick={() => setShowAddClientModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <UserPlus className="w-4 h-4" />
                        Add Client
                    </motion.button>
                </div>

                {/* Clients List */}
                <div className="space-y-4">
                    {clients.length === 0 ? (
                        <></>
                    ) : (
                        <></>
                    )}
                </div>

                {/* Add Client Modal */}
                {showAddClientModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white rounded-xl p-6 w-full max-w-md"
                        >
                            <h3 className="text-xl font-semibold text-gray-900 mb-5">Create New Client</h3>
                            <form onSubmit={handleAddClient} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                    <input
                                        type="text"
                                        value={newClientData.name}
                                        onChange={(e) => setNewClientData(prev => ({ ...prev, name: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="John Client"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                                    <input
                                        type="email"
                                        value={newClientData.email}
                                        onChange={(e) => setNewClientData(prev => ({ ...prev, email: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="client@example.com"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                                    <input
                                        type="tel"
                                        value={newClientData.phone}
                                        onChange={(e) => setNewClientData(prev => ({ ...prev, phone: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="+1234567890"
                                        required
                                    />
                                </div>

                                <div className="flex justify-end space-x-3 pt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowAddClientModal(false)}
                                        disabled={saving}
                                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2 disabled:opacity-70`}
                                    >
                                        {saving ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <UserPlus className="w-4 h-4" />
                                                Create Client
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LawyerCreateClient;